"use client";

import L from "leaflet";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { INDIA_BOUNDS, type FleetHealth } from "@/lib/devices/fleet-dashboard";
import { cn } from "@/lib/utils/cn";

import "leaflet/dist/leaflet.css";

export type FleetMapMarker = {
  id: string;
  label: string;
  uid: string;
  lat: number;
  lng: number;
  health: FleetHealth;
  approximate: boolean;
  lastSeen: string | null;
};

function markerStyle(health: FleetHealth): { color: string; fillColor: string } {
  switch (health) {
    case "healthy":
      return { color: "var(--semantic-success)", fillColor: "var(--semantic-success)" };
    case "degraded":
      return { color: "var(--semantic-warning)", fillColor: "var(--semantic-warning)" };
    case "offline":
      return { color: "var(--semantic-offline)", fillColor: "var(--semantic-offline)" };
    default:
      return { color: "var(--muted)", fillColor: "var(--muted)" };
  }
}

function activeInactiveMarkerStyle(active: boolean): { color: string; fillColor: string } {
  return active
    ? { color: "var(--semantic-success)", fillColor: "var(--semantic-success)" }
    : { color: "var(--semantic-error)", fillColor: "var(--semantic-error)" };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function popupHtml(m: FleetMapMarker): string {
  const approx = m.approximate
    ? '<p class="mt-ds-1 text-micro text-accent">Approximate position - set latitude / longitude in device metadata.</p>'
    : "";
  const last = m.lastSeen ? new Date(m.lastSeen).toLocaleString() : "-";
  return [
    '<div class="min-w-40 font-sans text-body text-foreground">',
    '<p class="m-0 font-semibold">' + escapeHtml(m.label) + '</p>',
    '<p class="mt-ds-1 font-mono text-caption text-muted">' + escapeHtml(m.uid) + '</p>',
    approx,
    '<p class="mt-ds-1 text-caption text-muted">Last seen: ' + escapeHtml(last) + '</p>',
    '</div>',
  ].join("");
}

const INDIA_CENTER = L.latLng(22.5, 79);
const DEFAULT_ZOOM = 4.5;

const INDIA_LAT_LNG_BOUNDS = L.latLngBounds(
  L.latLng(INDIA_BOUNDS.south, INDIA_BOUNDS.west),
  L.latLng(INDIA_BOUNDS.north, INDIA_BOUNDS.east),
);

type FleetIndiaMapProps = {
  markers: FleetMapMarker[];
  className?: string;
  variant?: "fleetHealth" | "activeInactive";
};

/**
 * India-focused fleet map. Uses imperative Leaflet (`L.map` + `map.remove()`) instead of
 * `react-leaflet` so React 18 Strict Mode / dynamic mounting cannot initialize the same node twice.
 */
export function FleetIndiaMap({ markers, className, variant = "fleetHealth" }: FleetIndiaMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef(markers);
  markersRef.current = markers;

  const applyMarkers = useCallback(() => {
    const map = mapRef.current;
    const group = layerRef.current;
    if (!map || !group) {
      return;
    }

    const list = markersRef.current;
    group.clearLayers();

    for (const m of list) {
      const { color, fillColor } =
        variant === "activeInactive"
          ? activeInactiveMarkerStyle(m.health !== "offline" && m.health !== "unknown")
          : markerStyle(m.health);
      const cm = L.circleMarker(L.latLng(m.lat, m.lng), {
        radius: 9,
        color,
        fillColor,
        fillOpacity: 0.92,
        weight: 2,
      });
      cm.bindPopup(popupHtml(m), { maxWidth: 320, className: "fleet-map-popup" });
      cm.addTo(group);
    }

    if (list.length === 0) {
      map.setView(INDIA_CENTER, DEFAULT_ZOOM);
    } else {
      const latLngs = list.map((m) => L.latLng(m.lat, m.lng));
      const b = L.latLngBounds(latLngs);
      if (b.isValid()) {
        map.fitBounds(b, { padding: [28, 28], maxZoom: 10 });
      }
    }
  }, [variant]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const map = L.map(el, {
      center: INDIA_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
      minZoom: 4,
      maxBounds: INDIA_LAT_LNG_BOUNDS.pad(0.08),
      maxBoundsViscosity: 0.85,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const group = L.layerGroup().addTo(map);
    mapRef.current = map;
    layerRef.current = group;

    applyMarkers();

    const onResize = () => {
      map.invalidateSize();
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      group.clearLayers();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [applyMarkers]);

  useEffect(() => {
    applyMarkers();
  }, [markers, applyMarkers]);

  return (
    <div className={cn("relative isolate overflow-hidden rounded-card border border-card-border bg-surface-secondary", className)}>
      <div
        ref={containerRef}
        className="z-[1] h-[var(--map-height)] w-full [&_.leaflet-container]:!h-full [&_.leaflet-container]:!w-full"
        aria-label="Fleet map India"
      />
      <div className="pointer-events-none absolute bottom-2 left-2 z-[500] flex flex-wrap gap-2 rounded-lg border border-border/80 bg-card/95 px-2 py-1.5 text-[0.65rem] shadow-sm">
        {variant === "activeInactive"
          ? (
              [
                ["active", "Active", activeInactiveMarkerStyle(true).fillColor],
                ["inactive", "Inactive", activeInactiveMarkerStyle(false).fillColor],
              ] as const
            ).map(([key, label, color]) => (
              <span key={key} className="inline-flex items-center gap-1 text-muted">
                <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                {label}
              </span>
            ))
          : (
              [
                ["healthy", "Healthy"],
                ["degraded", "Degraded"],
                ["offline", "Offline"],
                ["unknown", "Unknown"],
              ] as const
            ).map(([h, lab]) => (
              <span key={h} className="inline-flex items-center gap-1 text-muted">
                <span className="h-2 w-2 rounded-full" style={{ background: markerStyle(h).fillColor }} />
                {lab}
              </span>
            ))}
      </div>
    </div>
  );
}
